import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import logo from './logo.png';
import './downLoad.less';

const IOS_DOWNLOAD_URL = 'https://apps.apple.com/app/snailcle/id1603185754';
const ANDROID_DOWNLOAD_URL =
  'https://play.google.com/store/apps/details?id=com.pipishou.snailcycle';

type Platform = 'ios' | 'android' | null;

const getPlatform = (): Platform => {
  const userAgent = navigator.userAgent || navigator.vendor;

  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return 'ios';
  }

  if (/Android/i.test(userAgent)) {
    return 'android';
  }

  return null;
};

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 5v10m0 0-4-4m4 4 4-4M5 19h14"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type StoreButtonProps = {
  id: string;
  href: string;
  recommended: boolean;
  smallText: string;
  largeText: string;
  actionLabel: string;
  qrAlt: string;
  icon: JSX.Element;
};

const StoreButton = ({
  id,
  href,
  recommended,
  smallText,
  largeText,
  actionLabel,
  qrAlt,
  icon
}: StoreButtonProps) => {
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
    href
  )}`;

  return (
    <a
      id={id}
      href={href}
      className={`store-btn${recommended ? ' recommended' : ''}`}
      target="_blank"
      rel="noreferrer"
    >
      <div className="btn-content">
        <div className="icon-wrapper">{icon}</div>
        <div className="text-content">
          <span className="text-sm">{smallText}</span>
          <span className="text-lg">{largeText}</span>
        </div>
      </div>
      <div className="mobile-action-btn">
        <span>{actionLabel}</span>
        {/*<ArrowIcon />*/}
      </div>
      <div className="qr-wrapper">
        <img src={qrCodeUrl} alt={qrAlt} />
      </div>
    </a>
  );
};

export const DownLoad = () => {
  const intl = useIntl();
  const [platform, setPlatform] = useState<Platform>(null);

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'download.meta.title' });

    const currentPlatform = getPlatform();
    setPlatform(currentPlatform);

    if (!currentPlatform) {
      return undefined;
    }

    const targetUrl = currentPlatform === 'ios' ? IOS_DOWNLOAD_URL : ANDROID_DOWNLOAD_URL;
    const timer = window.setTimeout(() => {
      window.location.href = targetUrl;
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [intl]);

  const statusText = useMemo(() => {
    if (platform === 'ios') {
      return intl.formatMessage({ id: 'download.status.ios' });
    }

    if (platform === 'android') {
      return intl.formatMessage({ id: 'download.status.android' });
    }

    return '';
  }, [intl, platform]);

  return (
    <div className="download-page">
      <div className="bg-glow" />
      <div className="download-container">
        <div className="logo-wrapper">
          <img src={logo} alt="Snailcle App Icon" className="app-logo-img" />
        </div>

        <h1>{intl.formatMessage({ id: 'download.title' })}</h1>
        <p className="subtitle">{intl.formatMessage({ id: 'download.subtitle' })}</p>

        <div className={`status-text${statusText ? ' active' : ''}`}>{statusText}</div>

        <div className="download-options">
          <StoreButton
            id="btn-ios"
            href={IOS_DOWNLOAD_URL}
            recommended={platform === 'ios'}
            smallText={intl.formatMessage({ id: 'download.store.apple.small' })}
            largeText={intl.formatMessage({ id: 'download.store.apple.large' })}
            actionLabel={intl.formatMessage({ id: 'download.store.action' })}
            qrAlt={intl.formatMessage({ id: 'download.qr.ios' })}
            icon={<AppleIcon />}
          />
          <StoreButton
            id="btn-android"
            href={ANDROID_DOWNLOAD_URL}
            recommended={platform === 'android'}
            smallText={intl.formatMessage({ id: 'download.store.google.small' })}
            largeText={intl.formatMessage({ id: 'download.store.google.large' })}
            actionLabel={intl.formatMessage({ id: 'download.store.action' })}
            qrAlt={intl.formatMessage({ id: 'download.qr.android' })}
            icon={<GooglePlayIcon />}
          />
        </div>
      </div>
      <a href="https://www.snailcle.com/index/#/downloadsc" className="apk-download-link">
            Don't have Google Play? Download APK directly.
        </a>

      <footer>
        <div className="company-name">
          {intl.formatMessage(
            { id: 'download.footer.company' },
            { year: new Date().getFullYear() }
          )}
        </div>
      </footer>
    </div>
  );
};
